import { Injectable } from '@angular/core';
import { addDays, endOfWeek, getDay, getHours, getMinutes, set, startOfWeek } from 'date-fns';

import { IECRCalendar, IECRCar, IECRCarDetails, IECRSummaryConfig } from '@ruby/shared/models/ecr/ecr.interface';
import { NSDateUtils } from '@ruby/shared/services/utils/ns-date.utils';

@Injectable({
  providedIn: 'root'
})
export class ECRSummaryUtils {

  constructor() { }

  /**
   * Get Events
   *
   * @summary Validates monthly calendar data and returns events per week
   * @param calendar: IECRCalendar
   * @param cutOffDay: number
   * @param startDate: number
   * @returns Array<IECRCar>
   */
  getEvents(calendar: IECRCalendar, cutOffDay: number, startDate: number): Array<IECRCar> {
    const cars: Array<IECRCar> = [];
    const blockedWeeks: Set<number> = this.getBlockedWeeks(cutOffDay, calendar.startDate, calendar.cars);

    let startWeek: number = NSDateUtils.nsStartOfWeek(startDate).getTime();
    let endWeek: number = NSDateUtils.nsEndOfWeek(startDate).getTime();
    for (let i = 0; i < 5; i++) {
      const blockedWeek: boolean = blockedWeeks.has(startWeek);
      const ecrCar: IECRCar | undefined = calendar.cars.find((car: IECRCar) => NSDateUtils.convertToNSDate(car.startWeekDate).getTime() === startWeek);
      if (ecrCar) {
        cars.push({
          ...ecrCar,
          disabled: blockedWeek,
          startWeekDate: NSDateUtils.convertToNSDate(ecrCar.startWeekDate).getTime(),
          wkExpDate: NSDateUtils.convertToNSDate(ecrCar.wkExpDate).getTime()
        });
      } else {
        const newEvent: IECRCar = this.addNewEvents(startWeek, endWeek, blockedWeek);
        cars.push(newEvent);
      }
      startWeek = startOfWeek(addDays(endWeek, 1), { weekStartsOn: 1 }).getTime();
      endWeek = endOfWeek(startWeek, { weekStartsOn: 1 }).getTime();
    }
    return cars;
  }

  /**
   * Get Cut OffDate
   *
   * @summary Returns cutoff date for start week
   * @param startWeek: number
   * @param cutOffDay: number
   * @returns Date
   */
  getCutOffDate(startWeek: number, cutOffDay: number): Date {
    let weekCutOffDate = NSDateUtils.convertToNSDate(startWeek);
    if (getDay(startWeek) !== cutOffDay) {
      for (let i = 1; i < 7; i++) {
        if (getDay(addDays(weekCutOffDate, i)) === cutOffDay) {
          weekCutOffDate = addDays(weekCutOffDate, i);
          break;
        }
      }
    }
    return weekCutOffDate;
  }

  /**
   * Summary Config
   *
   * @summary Creates calendar summary config
   * @param calendar: IECRCalendar
   * @returns Array<IECRSummaryConfig>
   */
  summaryConfig(calendar: IECRCalendar): Array<IECRSummaryConfig> {
    const configs: Array<IECRSummaryConfig> = [];
    calendar.cars.forEach((car: IECRCar) => {
      if (car.disabled) {
        configs.push({
          disableNote: true,
          disableCancel: true,
          disableExpiration: true,
          car
        });
      } else {
        configs.push({
          disableNote: false,
          disableCancel: (!car.status || car.status === 'Request Canceled'),
          disableExpiration: car.carRequestList.every(request => request?.quantity < 1),
          car
        });
      }
    });
    return this.sortConfig(configs);
  }

  private sortConfig(configs: Array<IECRSummaryConfig>): Array<IECRSummaryConfig> {
    return configs.sort((a: IECRSummaryConfig, b: IECRSummaryConfig) => {
      const dateOne = NSDateUtils.convertToNSDate(a.car.startWeekDate).getTime();
      const dateTwo = NSDateUtils.convertToNSDate(b.car.startWeekDate).getTime();
      if (dateOne < dateTwo) {
        return -1;
      }
      if (dateOne > dateTwo) {
        return 1;
      }
      return 0;
    });
  }

  private getBlockedWeeks(cutOffDay: number, startDate: number, cars: Array<IECRCar>): Set<number> {
    const blockedWeeks: Set<number> = new Set();
    const today: Date = set(NSDateUtils.convertToNSDate(new Date()), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
    blockedWeeks.add(startDate);

    let startWeek: number = startOfWeek(startDate, { weekStartsOn: 1 }).getTime();
    let endWeek: number = endOfWeek(startDate, { weekStartsOn: 1 }).getTime();
    for (let i = 0; i < 4; i++) {
      const nextWeekStart: number = startOfWeek(addDays(endWeek, 1), { weekStartsOn: 1 }).getTime();
      const nextWeekDenied: boolean = this.isDeniedStatus(cars, nextWeekStart);
      const weekCutOffDate: Date = this.getCutOffDate(startWeek, cutOffDay);
      if (
        nextWeekDenied ||
        (today.getTime() > weekCutOffDate.getTime()) ||
        (today.getTime() === weekCutOffDate.getTime() && (getHours(new Date()) === 23) && (getMinutes(new Date()) > 55))
      ) {
        blockedWeeks.add(nextWeekStart);
      }
      startWeek = startOfWeek(addDays(endWeek, 1), { weekStartsOn: 1 }).getTime();
      endWeek = endOfWeek(startWeek, { weekStartsOn: 1 }).getTime();
    }
    return blockedWeeks;
  }

  private isDeniedStatus(cars: Array<IECRCar>, startWeek: number): boolean {
    const match: IECRCar | undefined = cars.find((car: IECRCar) => car.startWeekDate === startWeek);
    return !!(match && match.statusId === '4');
  }

  private addNewEvents(startWeek: number, endWeek: number, blocked: boolean): IECRCar {
    const request = { notes: '', quantity: 0 } as IECRCarDetails;
    return {
      carRequestList: Array.from({ length: 7 }, () => request),
      requested: 0,
      status: '',
      startWeekDate: startWeek,
      startWeekDay: 'Monday',
      wkExpDate: endWeek,
      disabled: blocked
    } as IECRCar;
  }
}
